<script>
	import { onMount, onDestroy } from 'svelte';
	import Chart from 'chart.js/auto';
	import { STAGE_COLORS, STAGE } from './DataParser.js';
	import { baseChartOptions, darkTooltip, COLORS } from './ChartTheme.js';

	export let ourRatios = null;   // { rem, light, deep, awake }
	export let samsungRatios = null;
	export let title = '수면 단계 비율 비교';

	let chartCanvas;
	let chart;

	const labels = ['Deep', 'Light', 'REM', 'Awake'];
	const colors = [STAGE_COLORS[STAGE.DEEP], STAGE_COLORS[STAGE.LIGHT], STAGE_COLORS[STAGE.REM], STAGE_COLORS[STAGE.AWAKE_IN_BED]];

	function createChart() {
		if (chart) chart.destroy();
		if (!chartCanvas) return;
		if (!ourRatios && !samsungRatios) return;

		const datasets = [];
		if (ourRatios) {
			datasets.push({
				label: 'Sleepwave',
				data: [ourRatios.deep, ourRatios.light, ourRatios.rem, ourRatios.awake],
				backgroundColor: colors.map(c => c + 'CC'),
				borderColor: colors,
				borderWidth: 0,
				borderRadius: 6,
				borderSkipped: false,
				barPercentage: 0.7,
				categoryPercentage: 0.65,
			});
		}
		if (samsungRatios) {
			datasets.push({
				label: 'Samsung',
				data: [samsungRatios.deep, samsungRatios.light, samsungRatios.rem, samsungRatios.awake],
				backgroundColor: colors.map(c => c + '55'),
				borderColor: colors.map(c => c + '88'),
				borderWidth: 1,
				borderRadius: 6,
				borderSkipped: false,
				borderDash: [3, 3],
				barPercentage: 0.7,
				categoryPercentage: 0.65,
			});
		}

		chart = new Chart(chartCanvas, {
			type: 'bar',
			data: { labels, datasets },
			options: {
				...baseChartOptions(),
				interaction: { mode: 'index', axis: 'y', intersect: false },
				indexAxis: 'y',
				plugins: {
					legend: {
						display: datasets.length > 1,
						position: 'top',
						align: 'end',
						labels: {
							color: COLORS.textSecondary,
							font: { size: 11 },
							boxWidth: 12,
							boxHeight: 8,
							padding: 16,
							borderRadius: 4,
						}
					},
					tooltip: {
						...darkTooltip,
						callbacks: {
							label: ctx => ` ${ctx.dataset.label}: ${ctx.parsed.x.toFixed(1)}%`,
							labelColor: function(ctx) {
								const idx = ctx.dataIndex;
								return {
									borderColor: colors[idx],
									backgroundColor: colors[idx],
									borderWidth: 2,
									borderRadius: 2,
								};
							}
						}
					}
				},
				scales: {
					x: {
						max: 100,
						grid: {
							color: COLORS.gridLine,
							drawBorder: false,
							tickLength: 0,
						},
						border: { display: false },
						ticks: {
							color: COLORS.textSecondary,
							font: { size: 11 },
							callback: val => `${val}%`,
						},
					},
					y: {
						grid: { display: false },
						border: { display: false },
						ticks: {
							color: COLORS.textPrimary,
							font: { size: 12, weight: '500' },
							padding: 8,
						},
					}
				}
			}
		});
	}

	$: if (chartCanvas && (ourRatios || samsungRatios)) {
		createChart();
	}

	onMount(() => {
		if (ourRatios || samsungRatios) createChart();
	});

	onDestroy(() => {
		if (chart) chart.destroy();
	});
</script>

<div class="chart-card">
	<div class="chart-header">
		<h5 class="chart-title">{title}</h5>
	</div>
	<div class="chart-body">
		{#if !ourRatios && !samsungRatios}
			<div class="empty-state">데이터가 없습니다</div>
		{:else}
			<div class="chart-container">
				<canvas bind:this={chartCanvas}></canvas>
			</div>
		{/if}
	</div>
</div>

<style>
	.chart-card {
		background: #ffffff;
		border: 1px solid #e2e8f0;
		border-radius: 16px;
		padding: 20px 24px 16px;
		margin-bottom: 20px;
		box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
	}
	.chart-header {
		margin-bottom: 12px;
	}
	.chart-title {
		font-size: 15px;
		font-weight: 600;
		color: #334155;
		margin: 0;
	}
	.chart-body {
		position: relative;
	}
	.chart-container {
		height: 200px;
	}
	.empty-state {
		text-align: center;
		color: #94a3b8;
		padding: 48px 0;
		font-size: 14px;
	}
</style>
